
import React, { useState, useRef, useEffect } from 'react';
import Select, { components } from 'react-select';
import './multi.scss'; // Assuming you’ll adapt the SCSS from editable-grid.module.scss

function DynamicMultiSelect({ options, displayValue, selectedValues, setSelectedCategoryIds, selectedCategoryIds, placeholder, editable }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectRef = useRef(null);

  // Handle selection change
  const handleChange = (selectedOptions) => {
    const newValue = selectedOptions || [];
    const updatedIds = newValue.map(item => 
      displayValue === "niceName" ? item.id : item.id
    );
    setSelectedCategoryIds(updatedIds);
    setIsMenuOpen(newValue.length < options.length); // Keep menu open if more options can be selected
  };

  // Menu open/close handlers
  const handleMenuOpen = () => setIsMenuOpen(true);
  const handleMenuClose = () => setIsMenuOpen(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Custom styles to match MultiSelectCell
  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      color: '#fff',
      boxShadow: 'none',
      borderRadius: '4px',
      minHeight: '36px',
      width: '100%',
      padding: '2px',
      cursor: editable ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: 'transparent',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#2E3033',
      color: '#fff',
      width: '100%',
      borderRadius: '4px',
      marginTop: '2px',
      border: 'none',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      border: 'none',
     '&::-webkit-scrollbar': {
    width: '5px'
  },
  
  '&::-webkit-scrollbar-track' : {
    background: '#FFFFFF14'  
  },
' &::-webkit-scrollbar-thumb': {
    background:  "#D22F26",
    borderRadius: "50px", 
  },
  '&::-webkit-scrollbar-thumb:hover' : {
    background: '#D22F26'
  }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#E9E9E9' : state.isFocused ? '#444' : 'transparent',
      color: state.isSelected ? '#1A1A1A' : '#fff',
      padding: '8px',
      cursor: 'pointer',
      border: 'none',
      '&:hover': {
        backgroundColor: state.isSelected ? '#E9E9E9' : '#444',
        borderColor: 'transparent',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#555',
      margin: '2px',
      border: 'none',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#fff',
      padding: '2px 4px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#fff',
      padding: '2px',
      border: 'none',
      cursor: editable ? 'not-allowed' : 'pointer',
      '&:hover': {
        backgroundColor: editable ? '#555' : '#777',
        color: '#fff',
        borderColor: 'transparent',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#aaa',
    }),
    input: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: 'flex',
      flexWrap: 'wrap',
      padding: '2px',
      fontSize:"16px",
    }),
  };

  // Custom option component with checkbox-like style
  const CustomOption = ({ data, isSelected, innerRef, innerProps }) => (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#E9E9E9' : 'transparent',
      }}
    >
      <div
        style={{
          width: '18px',
          height: '18px',
          border: isSelected ? 'none' : '1px solid #606264',
          borderRadius: '4px',
          marginRight: '8px',
          backgroundColor: isSelected ? '#1A1A1A' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isSelected && (
          <span
            style={{
              color: '#E9E9E9',
              fontSize: '14px',
              lineHeight: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✓
          </span>
        )}
      </div>
      {data[displayValue] || data.name}
    </div>
  );

  // Filter selected values based on selectedCategoryIds
  const selected = options?.filter(option => 
    selectedCategoryIds?.includes(option.id)
  );

  return (
    <div style={{ width: '100%', backgroundColor:'#1c1c1c' }} ref={selectRef}>
      <Select
        isMulti
        options={options}
        value={selected}
        onChange={handleChange}
        getOptionLabel={(option) => option[displayValue] || option.name}
        getOptionValue={(option) => option.id}
        styles={customStyles}
        className="multiselect-field"
        placeholder={placeholder}
        components={{ Option: CustomOption }}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        menuIsOpen={isMenuOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        isDisabled={editable} // Maps to disable prop from original
      />
    </div>
  );
}

export default DynamicMultiSelect;